import { createElement, useEffect, useState } from 'react';

import type {
  ComponentType,
  CSSProperties,
  ElementType,
  ReactElement,
} from 'react';

interface MediaWithErrorHandlingProps<T extends ElementType | ComponentType> {
  /**
   * The element or component to render (e.g., 'img', 'video', or a styled component).
   */
  as: T;
  /**
   * The source URL for the media.
   */
  src?: string;
  /**
   * Inline styles to apply to the element.
   */
  style?: CSSProperties;
  /**
   * Additional props to pass through to the underlying element/component.
   */
  [key: string]: unknown;
}

export function MediaWithErrorHandling<T extends ElementType | ComponentType>({
  as: Component,
  src,
  style,
  ...props
}: MediaWithErrorHandlingProps<T>): ReactElement {
  const [hasError, setHasError] = useState(false);

  // Reset error state when src changes
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const handleError = () => {
    setHasError(true);
  };

  const shouldHide = !src || hasError;

  const finalStyle = shouldHide ? { ...(style ?? {}), display: 'none' } : style;

  return createElement(Component, {
    src,
    onError: handleError,
    style: finalStyle,
    ...props,
  });
}
